<template>
  <v-dialog
    v-model="active"
    width="1000px"
  >
    <template v-slot:activator="{ on }">
      <slot name="activator" v-bind="{ on }"></slot>
    </template>
    <v-card>
      <v-card-title
        class="title grey lighten-4"
        primary-title
      >
        Upload
      </v-card-title>
      <v-skeleton-loader
        v-if="!setupFinished"
        :loading="!setupFinished"
        type="article"
        class="mx-auto flex-fill"
        width="100%"
      />
      <v-fade-transition>
        <v-container
          v-if="setupFinished"
          class="fill-height"
        >
          <!-- Images -->
          <v-row
            justify="center"
            align="center"
            class="ma-0 my-2"
          >
            <v-card
              @dragover.prevent.stop="onDragOver"
              outlined
              width="100%"
              @click="onImageInputClick"
              @dragenter.passive.stop="onDragEnter"
              @dragexit.stop="onDragExit"
              @dragleave.stop="onDragLeave"
              @drop="onDrop"
              :loading="imageUploader.evaluating"
              class="dropzone"
            >
              <v-card-text>
                <v-row
                  justify="center"
                  align="center"
                  class="fill-height ma-0"
                >
                  <input
                    type="file"
                    accept="image/*"
                    ref="image-input"
                    @change="onImageInputChange"
                    class="image-input"
                    multiple
                  >
                  <empty-state
                    :color="isDragging ? 'accent' : ''"
                  >
                    <template v-slot:icon>
                      <v-icon :color="isDragging ? 'accent' : ''">mdi-cloud-upload</v-icon>
                    </template>
                    <template v-slot:description>
                      {{imageInputDescription}}
                    </template>
                    <template v-slot:overlay>
                      <v-overlay
                        :value="isDragging"
                        absolute
                        opacity=".8"
                      >
                        <span class="subtitle-1 font-weight-bold white--text">Solte os arquivos para adicioná-los ao upload</span>
                      </v-overlay>
                    </template>
                  </empty-state>
                </v-row>
              </v-card-text>
            </v-card>
          </v-row>
          <v-row
            justify="center"
            align="center"
            class="ma-0 my-2"
          >
            <v-card
              width="100%"
              elevation="10"
            >
              <v-row
                align="center"
                justify="space-between"
                class="ma-0"
              >
                <v-card-title class="title">Upload de imagens</v-card-title>
                <transition name="error">
                  <div class="error--text" v-if="this.$v.$dirty && this.$v.uploadedImages.$invalid">
                    {{uploadedImagesError}}
                  </div>
                  <div class="light-blue--text" v-if="imageUploader.isFinishingStream">
                    Estamos finalizando o upload, por favor aguarde.
                  </div>
                  <div class="error--text" v-if="imageUploader.deleting">
                    Estamos deletando sua image, por favor aguarde.
                  </div>
                </transition>
                <div class="body-1 ma-4">
                  <b>Arquivos Upados {{ imageUploader.uploadedQueue.length }}/{{ imageUploader.fullImagesList.length
                     }}</b>
                </div>
              </v-row>
              <v-divider></v-divider>
              <v-card-text>
                <v-row
                  justify="space-between"
                  align="center"
                  class="ma-0"
                >
                  <v-col
                    cols="11"
                  >
                    <v-progress-linear
                      :indeterminate="imageUploader.isFinishingStream"
                      :query="imageUploader.isFinishingStream"
                      :value="imageUploader.progress"
                      rounded
                    />
                  </v-col>
                  <v-col
                    cols="1"
                  >
                    <div>{{imageUploader.progress}}%</div>
                  </v-col>
                </v-row>
                <v-row
                  v-if="imageUploader.loading"
                  justify="center"
                  align="center"
                  class="ma-0 my-10"
                >
                  <v-progress-circular
                    indeterminate
                    color="accent"
                  />
                  <div class="caption ml-4">Carregando imagens...</div>
                </v-row>
                <v-row
                  v-else
                  align="center"
                  class="ma-0"
                >
                  <v-col
                    v-for="img in currImagesPage"
                    :key="img.name"
                    cols="6"
                    xs="3"
                    sm="3"
                    md="4"
                    lg="4"
                    xl="4"
                  >
                    <v-card
                      hover
                      class="pa-2 image-card"
                    >
                      <v-row
                        justify="space-between"
                        align="center"
                        class="ma-0 flex-nowrap"
                      >
                        <v-icon class="px-1" color="red">mdi-image</v-icon>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <span v-on="on" class="body-2 px-1 text-break text-truncate">{{img.name}}</span>
                          </template>
                          <span>{{img.name}}</span>
                        </v-tooltip>
                        <transition name="fade" mode="out-in">
                          <v-icon
                            v-if="checkImageUploadFinished(img)"
                            color="success"
                            class="px-1"
                          >
                            mdi-check-circle
                          </v-icon>
                          <v-progress-circular
                            v-else
                            :indeterminate="img.uploadStarted && img.uploadProgress === 0"
                            :value="img | getCurrImgProgress"
                            color="primary"
                            class="px-1"
                            width="3"
                          >
                            <span class="upload-progress__text">{{img | getCurrImgProgress}}</span>
                          </v-progress-circular>
                        </transition>
                      </v-row>
                      <v-btn
                        v-if="!imageUploader.uploading"
                        @click="() => {imageUploader.remove(img)}"
                        small
                        icon
                        :loading="imageUploader.deleting"
                        absolute
                        color="error"
                        elevation="0"
                        class="delete-image-btn"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row
                  v-if="maxPage > 1"
                  align="center"
                  class="ma-0 my-2"
                >
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @on="on"
                        :disabled="currPageIdx <= 0"
                        @click="() => {setPage(0)}"
                        icon
                        class="mx-2"
                      >
                        <v-icon>mdi-page-first</v-icon>
                      </v-btn>
                    </template>
                    <span>Primeira página</span>
                  </v-tooltip>
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @on="on"
                        :disabled="currPageIdx <= 0"
                        @click="lastPage"
                        icon
                        class="mx-2"
                      >
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                    </template>
                    <span>Página anterior</span>
                  </v-tooltip>
                  <v-spacer/>
                  <span class="subtitle-2">Página: {{currPageIdx + 1}}/{{maxPage}}</span>
                  <v-spacer/>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :disabled="nextPageIsDisabled"
                        @click="nextPage"
                        @on="on"
                        class="mx-2"
                        icon
                      >
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </template>
                    <span>Próxima página</span>
                  </v-tooltip>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        @on="on"
                        :disabled="nextPageIsDisabled"
                        @click="() => {setPage(maxPage - 1)}"
                        icon
                        class="mx-2"
                      >
                        <v-icon>mdi-page-last</v-icon>
                      </v-btn>
                    </template>
                    <span>Última página</span>
                  </v-tooltip>
                </v-row>
                <v-row
                  align="center"
                  class="ma-0"
                >
                  <v-btn
                    color="warning"
                    outlined
                    block
                    :loading="imageUploader.evaluating"
                    @click="() => { imageUploader.start() }"
                    :disabled="imageUploader.uploading || !imageUploader.toUploadQueue.length"
                  >
                    Começar Upload
                    <template v-slot:loader>
                      <span>Verificando imagens {{imageUploader.totalEvaluated}}/{{imageUploader.totalToEvaluate}}</span>
                    </template>
                  </v-btn>
                </v-row>
              </v-card-text>
            </v-card>
          </v-row>

          <!-- Flight Log -->
          <v-row
            v-if="hasFlightLog"
            justify="center"
            align="center"
            class="ma-0 my-2"
          >
            <v-card
              width="100%"
              elevation="10"
            >
              <v-row
                align="center"
                justify="space-between"
                class="ma-0"
              >
                <v-card-title class="title">Upload Log de Voo</v-card-title>
                <transition name="error">
                  <div
                    v-if="this.$v.$dirty && this.$v.isFlightLogUploaded.$invalid"
                    class="error--text"
                  >
                    {{isFlightLogUploadedError}}
                  </div>
                </transition>
              </v-row>
              <v-card-text>
                <v-row
                  justify="space-between"
                  align="center"
                  class="ma-0"
                >
                  <v-col
                    cols="11"
                  >
                    <v-progress-linear
                      :value="flightLogUploader.progress"
                      rounded
                    >
                    </v-progress-linear>
                  </v-col>
                  <v-col
                    cols="1"
                    align-self="end"
                  >
                    <div>{{flightLogUploader.progress}}%</div>
                  </v-col>
                </v-row>
                <v-row
                  class="ma-0"
                >
                  <v-file-input
                    v-model="flightLogInput"
                    :label="flightLogInputLabel"
                    outlined
                    clearable
                    @change="(file) => {flightLogUploader.add(file)}"
                  />
                </v-row>
                <v-row
                  align="center"
                  class="ma-0"
                >
                  <v-btn
                    color="warning"
                    outlined
                    block
                    :loading="flightLogUploader.uploading"
                    @click="() => { flightLogUploader.upload() }"
                    :disabled="!flightLogUploader.canUpload"
                  >
                    Começar Upload
                    <template v-slot:loader>
                      <span>Carregando log de voo...</span>
                    </template>
                  </v-btn>
                </v-row>
              </v-card-text>
            </v-card>
          </v-row>

          <!-- Gcp -->
          <v-row
            v-if="hasGcp"
            justify="center"
            align="center"
            class="ma-0 my-2"
          >
            <v-card
              width="100%"
              elevation="10"
            >
              <v-row
                align="center"
                justify="space-between"
                class="ma-0"
              >
                <v-card-title class="title">Upload Pontos de Controle</v-card-title>
                <transition name="error">
                  <div
                    v-if="this.$v.$dirty && this.$v.isGcpUploaded.$invalid"
                    class="error--text"
                  >
                    {{isGcpUploadedError}}
                  </div>
                </transition>
              </v-row>
              <v-card-text>
                <v-row
                  justify="space-between"
                  align="center"
                  class="ma-0"
                >
                  <v-col
                    cols="11"
                  >
                    <v-progress-linear
                      :value="gcpUploader.progress"
                      rounded
                    >
                    </v-progress-linear>
                  </v-col>
                  <v-col
                    cols="1"
                    align-self="end"
                  >
                    <div>{{gcpUploader.progress}}%</div>
                  </v-col>
                </v-row>
                <v-row
                  class="ma-0"
                >
                  <v-file-input
                    v-model="gcpInput"
                    label="Clique aqui e insira um arquivo do tipo .txt"
                    outlined
                    clearable
                    @change="(file) => { gcpUploader.add(file) }"
                  />
                </v-row>
                <v-row
                  align="center"
                  class="ma-0"
                >
                  <v-btn
                    color="warning"
                    outlined
                    block
                    :loading="gcpUploader.uploading"
                    @click="() => {gcpUploader.upload()}"
                    :disabled="!gcpUploader.canUpload"
                  >
                    Começar Upload
                    <template v-slot:loader>
                      <span>Carregando gcp...</span>
                    </template>
                  </v-btn>
                </v-row>
              </v-card-text>
            </v-card>
          </v-row>
          <v-row
            justify="start"
            align="center"
            class="ma-0 my-2"
          >
            <v-checkbox
              label="Começar processamento ao finalizar o upload"
              v-model="mustStartProcessingOnFinish"
            />
          </v-row>
        </v-container>
      </v-fade-transition>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn
          color="primary"
          text
          @click="active = false"
        >
          Fechar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!mustStartProcessingOnFinish"
          color="primary"
          @click="onProcessClick"
        >
          Processar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="js">
  import {validationMixin} from "vuelidate"
  import EmptyState from "../Helpers/EmptyState"
  import {cloneDeep, gte, isEmpty, isEqual, isNull, isUndefined} from "lodash/lang"
  import {
    calculateFlightOrderProcessingData,
    getFlightOrderUploadedFlightLog, getFlightOrderUploadedGcp,
    listFlightOrderUploadedImages, startFlightOrderProcessing,
  } from "../../api/api-project"
  import {CAMERA_TYPES, GEOREFERENCE_TYPES, PROCESSING_TYPES} from "../../helpers/processings/constants"
  import {IMAGES_PER_PAGE} from "../../helpers/upload/constants"
  import {FlightLogUploader, GcpUploader, ImageUploader} from "../../helpers/flight-provider/mixins"
  import {UPLOADER_URL} from "../../globals"
  import UploadFileHolder from "../../helpers/upload/file-holder"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../helpers/notify"

  export default {
    name: "FlightOrderUploadDialog",
    props: {
      processing: {
        type: Object,
        required: true,
      },
    },
    mixins: [validationMixin],
    components: {EmptyState},
    data: () => ({
      active: false,

      setupFinished: false,

      imageUploader: null,
      flightLogUploader: null,
      gcpUploader: null,

      flightLogInput: null,
      gcpInput: null,

      isDirty: false,
      isDragging: false,

      hasImageError: false,
      hasFlightLogError: false,

      mustStartProcessingOnFinish: false,

      // Pagination
      currPageIdx: 0,
      currPageImages: [],
    }),
    validations() {
      return {
        imageUploader: {
          uploadedImages: {
            minLength: function () {
              if (this.processingTypeIsFP)
                return this.uploadedImages.length >= 1
              else
                return this.uploadedImages.length === 1
            },
          },
        },
        ...((this.hasFlightLog) && {
          flightLogUploader: {
            complete: {
              complete: function () {
                return this.flightLogUploader.complete
              },
            },
          },
        }),
        ...((this.hasGcp) && {
          gcpUploader: {
            complete: {
              complete: function () {
                return this.gcpUploader.complete
              },
            },
          },
        }),
      }
    },
    methods: {
      // Image Upload control
      onImageInputClick() {
        if (!this.imageUploader.canUpload) return
        this.$refs["image-input"].click()
      },
      onImageInputChange(event) {
        this.hasImageError = false
        this.addImagesToUpload(Array.from(event.target.files))
      },
      addImagesToUpload(files) {
        if (isEmpty(files) || this.imageUploader.loading) return
        const checkGeoTag = this.processingTypeIsFP && !this.hasFlightLog
        this.imageUploader?.add(files, this.isMultispectral, checkGeoTag, this.hasFlightLog)
      },

      onUploadFinished() {
        if (!(this.isUploadFinished && this.mustStartProcessingOnFinish)) return

        setTimeout(() => {
          calculateFlightOrderProcessingData(this.$apiAuth.getAuthToken(), this.processing["id_processing"])
            .finally(() => {
              startFlightOrderProcessing(this.$apiAuth.getAuthToken(), this.processing["id_processing"])
                .then(() => {
                  this.$router.go(0)
                })
            })
        }, 100)
      },
      onProcessClick() {
        calculateFlightOrderProcessingData(this.$apiAuth.getAuthToken(), this.processing["id_processing"])
          .finally(() => {
            startFlightOrderProcessing(this.$apiAuth.getAuthToken(), this.processing["id_processing"])
              .then(() => {
                this.$router.go(0)
              })
          })
      },

      // Image Drop files section events
      onDrop(event) {
        event.preventDefault()
        this.hasImageError = false
        this.isDirty = false

        if (!this.imageUploader?.canUpload) {
          return
        }

        this.isDragging = false

        if (!event.dataTransfer.items) {
          return
        }

        const fileList = this.getFilesFromDataTransfer(event.dataTransfer)

        this.addImagesToUpload(fileList)
      },
      onDragEnter() {
        if (!this.imageUploader?.canUpload) return
        this.isDragging = true
      },
      onDragExit() {
        if (!this.isFirefox && !this.isOtherBrowser) return

        if (!this.imageUploader?.canUpload) return
        this.isDragging = false
      },
      onDragOver(event) {
        event.preventDefault()
      },
      onDragLeave() {
        if (!this.imageUploader?.canUpload) return
        this.isDragging = false
      },

      // Pages
      // Paginação para as imagens
      nextPage() {
        if (this.currPageIdx >= this.maxPage - 1) return
        this.currPageIdx++
      },
      lastPage() {
        if (this.currPageIdx - 1 < 0) return
        this.currPageIdx--
      },
      setPage(idx) {
        if (isUndefined(idx) || isNull(idx) || idx >= this.maxPage || idx < 0) return
        this.currPageIdx = idx
      },

      // Helpers
      getFilesFromDataTransfer(dataTransfer) {
        const addedFiles = []
        for (const item of dataTransfer.items) {
          if (item.kind !== "file") {
            continue
          }

          addedFiles.push(item.getAsFile())
        }

        return addedFiles
      },
      checkImageUploadFinished(img) {
        return img.uploadCompleted && gte(img.uploadProgress, 100)
      },
      onMounted() {
        this.imageUploader = new ImageUploader(
          `${UPLOADER_URL}/upload/image/flight-order/${this.processing["id_processing"]}`, this.processing["id_processing"], this.setPage, this.onUploadFinished,
        )
        this.flightLogUploader = new FlightLogUploader(
          `${UPLOADER_URL}/upload/flight-log/flight-order/${this.processing["id_processing"]}`, this.processing["ab_georeference_type"], this.onUploadFinished,
        )
        this.gcpUploader = new GcpUploader(
          `${UPLOADER_URL}/upload/gcp/flight-order/${this.processing["id_processing"]}`, this.onUploadFinished,
        )

        this.imageUploader.loading = true

        const authToken = this.$apiAuth.getAuthToken()
        listFlightOrderUploadedImages(authToken, this.processing["id_processing"])
          .then((res) => {
            const loadedFiles = res.data.data.files
            this.imageUploader.uploadedQueue = loadedFiles.map((obj) => {
              const nameSplit = obj.name.split("/")
              const fileName = nameSplit[nameSplit.length - 1]

              const uploaderFileHolder = new UploadFileHolder({name: fileName, size: obj.size})
              uploaderFileHolder.uploadProgress = 100
              uploaderFileHolder.uploadCompleted = true
              return uploaderFileHolder
            })

            if (this.imageUploader.uploadedQueue.length > 0) this.imageUploader.progress = 100
            this.imageUploader.loading = false
          })
          .catch((err) => {
            console.log(err)
            this.$notify(new NotificationOptions({
              message: "Erro ao carregar imagens upadas, atualize a página.",
              type: NOTIFICATION_TYPES.WARNING,
            }))
            this.imageUploader.loading = false
          })

        getFlightOrderUploadedFlightLog(authToken, this.processing["id_processing"])
          .then((res) => {
            const flightLogs = res.data.data.files

            if (isEmpty(flightLogs)) return

            this.flightLogUploader.uploadFileHolder = new UploadFileHolder({
              name: "Log de voo upado",
              size: flightLogs[0].size,
            })
            this.flightLogUploader.complete = true
            this.flightLogUploader.progress = 100

            this.flightLogInput = new File([], this.flightLogUploader.uploadFileHolder.name)
          })

        getFlightOrderUploadedGcp(authToken, this.processing["id_processing"])
          .then((res) => {
            const gcps = res.data.data.files

            if (isEmpty(gcps)) return

            this.gcpUploader.uploadFileHolder = new UploadFileHolder({
              name: "Ponto de controle upado",
              size: gcps[0].size,
            })
            this.gcpUploader.complete = true
            this.gcpUploader.progress = 100

            this.gcpInput = new File([], this.gcpUploader.uploadFileHolder.name)
          })

        this.setupFinished = true
      },
    },
    filters: {
      getCurrImgProgress(img) {
        return img.uploadProgress ? Math.trunc(img.getUploadProgress()) : 0
      },
    },
    computed: {
      uploadedImagesError() {
        let error = null
        if (!this.$v.uploadedImages.$dirty) return error
        let errDesc = "Apenas é possível criar um processamento do tipo 'Imagens de Drone' com mais de 8 imagens."
        !this.$v.uploadedImages.minLength && (error = errDesc)
        return error
      },
      isFlightLogUploadedError() {
        let error = null
        if (!this.$v.isFlightLogUploaded.$dirty) return error
        !this.$v.isFlightLogUploaded.complete && (error = "É necessário fazer o upload do log de voo para continuar")
        return error
      },
      isGcpUploadedError() {
        let error = null
        if (!this.$v.isGcpUploaded.$dirty) return error
        !this.$v.isGcpUploaded.complete && (error = "É necessário fazer o upload do gcp para continuar")
        return error
      },

      imageInputDescription() {
        if (isEmpty(this.processing))
          return null
        if (this.isMultispectral)
          return "Arraste ou clique para adicionar imagens do tipo .tif, .jpeg ou .zip"
        else
          return "Arraste ou clique para adicionar imagens do tipo .png, .jpg ou .zip"
      },
      flightLogInputLabel() {
        if (isEmpty(this.processing))
          return null
        if (!this.hasFlightLog)
          return null
        if (isEqual(this.processing["ab_georeference_type"], GEOREFERENCE_TYPES.THREE_D_ROBOTICS))
          return "Clique aqui e insira um arquivo do tipo .log"
        else
          return "Clique aqui e insira um arquivo do tipo .txt"
      },
      isUploadFinished() {
        return !(
          (this.imageUploader.uploading || this.flightLogUploader.uploading || this.gcpUploader.uploading) ||
          (this.imageUploader.loading || this.imageUploader.uploadedQueue.length === 0) ||
          (this.hasFlightLog && !this.flightLogUploader.complete) ||
          (this.hasGcp && !this.gcpUploader.complete) ||
          !isEmpty(this.imageUploader.toUploadQueue)
        )
      },

      // Helpers
      isFirefox() {
        return typeof InstallTrigger !== "undefined"
      },
      isChrome() {
        return !!window.chrome && (!!window.chrome["webstore"] || !!window.chrome["runtime"])
      },
      isOtherBrowser() {
        return !this.isFirefox && !this.isChrome
      },

      // Image Pages
      maxPage() {
        return Math.ceil(this.imageUploader?.fullImagesList.length / IMAGES_PER_PAGE)
      },
      currImagesPage() {
        const initIndex = this.currPageIdx * IMAGES_PER_PAGE
        const fullImagesListClone = cloneDeep(this.imageUploader?.fullImagesList)
        return fullImagesListClone.slice(initIndex, initIndex + IMAGES_PER_PAGE)
      },
      nextPageIsDisabled() {
        return gte(this.currPageIdx, (this.maxPage - 1))
      },

      // Processing Info
      isMultispectral() {
        return this.processing && isEqual(this.processing["ab_camera_type"], CAMERA_TYPES.MULTISPECTRAL)
      },
      hasFlightLog() {
        if (!this.processing)
          return false
        return (
          isEqual(this.processing["ab_processing_type"], PROCESSING_TYPES.FULL_PROCESSING)
          && !isEqual(this.processing["ab_georeference_type"], GEOREFERENCE_TYPES.NOT_SET)
        )
      },
      hasGcp() {
        if (!this.processing)
          return false
        return (
          isEqual(this.processing["ab_processing_type"], PROCESSING_TYPES.FULL_PROCESSING)
          && this.processing["bl_gcp"]
        )
      },
      processingTypeIsFP() {
        if (isEmpty(this.processing))
          return false
        return isEqual(this.processing["ab_processing_type"], PROCESSING_TYPES.FULL_PROCESSING)
      },
    },
    mounted() {
      this.onMounted()
    },
  }
</script>

<style scoped lang="scss">

  .image-input {
    display: none;
  }

  .image-card {
    .delete-image-btn {
      top: 0 !important;
      right: 0 !important;
      transform: translate(50%, -50%) scale(0) !important;
      transition: transform 250ms cubic-bezier(0.19, 1, 0.22, 1) !important;
    }

    &:hover {
      .delete-image-btn {
        transform: translate(50%, -50%) scale(1) !important;
      }
    }
  }

  .actions {
    .v-btn {
      margin: 0 5px;
    }
  }

  .fade-enter-active {
    transition: transform .2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .fade-leave-active {
    transition: transform .2s cubic-bezier(0.6, -0.28, 0.735, 0.045);
  }

  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
  {
    transform: scale(0);
  }

  .error-enter-active, .error-leave-active {
    transition: all 150ms ease-in;
  }

  .error-enter, .error-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }

  .list-enter-active, .list-leave-active {
    transition: all 1s;
  }

  .list-enter, .list-leave-to {
    opacity: 0.4;
    transform: translateX(100%);
  }

  .upload-progress__text {
    font-size: 10px;
  }

  .dropzone {
    * {
      pointer-events: none !important;
    }
  }

</style>
